import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { resendConfirmationEmail } from '@/core/auth/supabase-auth';

const COOLDOWN_SECONDS = 60;

interface VerifyState {
  email?: string;
}

const EmailVerificationPage = () => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const email = (location.state as VerifyState | null)?.email;

  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email || sending || cooldown > 0) return;
    setErrorMsg(null);
    setSending(true);
    try {
      await resendConfirmationEmail(email);
      setCooldown(COOLDOWN_SECONDS);
    } catch {
      setErrorMsg(t('emailVerification.resendError'));
    } finally {
      setSending(false);
    }
  };

  const buttonLabel = sending
    ? t('emailVerification.resendingButton')
    : cooldown > 0
      ? t('emailVerification.resendCooldown', { seconds: cooldown })
      : t('emailVerification.resendButton');

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-4">
      <div className="w-full max-w-md p-6 sm:p-8 space-y-6 bg-card rounded-lg shadow-md">
        <div className="flex justify-center">
          <img src="/logo-header-light.svg" alt="Forecast Engine" className="h-16 w-auto" />
        </div>
        <h1 className="text-2xl font-bold text-center text-foreground">
          {t('emailVerification.title')}
        </h1>
        <div className="space-y-3 text-foreground text-center">
          <p className="text-base">
            {email
              ? t('emailVerification.message', { email })
              : t('emailVerification.messageNoEmail')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('emailVerification.instruction')}
          </p>
        </div>

        <div className="rounded-md bg-accent/30 p-3 text-sm text-accent-foreground">
          {t('emailVerification.checkSpam')}
        </div>

        <div className="space-y-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={!email || sending || cooldown > 0}
            className="w-full px-4 py-2 text-sm font-medium text-primary-foreground rounded-md shadow-sm bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {buttonLabel}
          </button>
          {errorMsg && (
            <p className="text-xs text-red-600 text-center">{errorMsg}</p>
          )}
        </div>

        <Link
          to="/login"
          className="block text-center text-sm text-foreground hover:underline"
        >
          {t('emailVerification.backToLogin')}
        </Link>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
